import { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getDocs, collection, query, orderBy } from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import Toast from 'react-native-toast-message';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AdminInput from '../AdminInput';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import {
  SegmentedControl,
  useModalWidth,
  useResponsive,
} from '../../pages/AdminPanel/Products/formStyles';

// Linha de escolha ÚNICA. Antes eram `CheckBox` do react-native-elements, o que
// sugeria seleção múltipla num campo que aceita só um destino.
const SelectableRow = ({ order, title, selected, onPress, last }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="radio"
    accessibilityState={{ selected }}
    style={({ hovered }) => [
      styles.row,
      !last && styles.rowDivider,
      hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
      selected && { backgroundColor: ADMIN_COLORS.accentSoft },
    ]}
  >
    <View style={styles.orderPill}>
      <Text style={styles.orderText}>{order}</Text>
    </View>
    <Text style={styles.rowTitle} numberOfLines={1}>
      {title}
    </Text>
    <Ionicons
      name={selected ? 'radio-button-on' : 'radio-button-off'}
      size={18}
      color={selected ? ADMIN_COLORS.accent : ADMIN_COLORS.border}
    />
  </Pressable>
);

const POSITION_OPTIONS = [
  {
    value: 'top',
    label: 'No topo',
    description: 'Entra como o primeiro conteúdo.',
    icon: 'arrow-up-outline',
  },
  {
    value: 'bottom',
    label: 'No final',
    description: 'Entra como o último conteúdo.',
    icon: 'arrow-down-outline',
  },
  {
    value: 'custom',
    label: 'Escolher posição',
    description: 'Você define o número da posição.',
    icon: 'list-outline',
  },
];

export default function CopyContentModal({
  visible,
  onDismiss,
  mode,
  sourceCategoryId,
  sourceCardId,
  videoDocId,
  onSuccess,
}) {
  const modalWidth = useModalWidth();
  const { isMobile } = useResponsive();

  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(sourceCategoryId);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [copying, setCopying] = useState(false);
  const [positionMode, setPositionMode] = useState('top'); // 'top' | 'bottom' | 'custom'
  const [customPosition, setCustomPosition] = useState('1'); // string pra input

  const isVideo = mode === 'video';

  useEffect(() => {
    if (visible) {
      loadCategories();
      if (isVideo) loadModules(sourceCategoryId);
      setSelectedCategory(sourceCategoryId);
      setSelectedModule(null);

      // ✅ defaults
      setPositionMode('top');
      setCustomPosition('1');
    }
  }, [visible]);

  const formatOrder = (n) =>
    typeof n === 'number' && n !== Number.MAX_SAFE_INTEGER
      ? String(n).padStart(2, '0')
      : '—';

  async function loadCategories() {
    setLoadingCategories(true);
    try {
      const q = query(collection(firestore, 'categories'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        order: doc.data().order ?? Number.MAX_SAFE_INTEGER,
      }))
        // fallback caso algum não tenha `order`
        .sort((a, b) => a.order - b.order);
      setCategories(list);
    } catch (e) {
      console.error('Erro ao carregar categorias', e);
      Toast.show({
        text1: 'Erro ao carregar categorias.',
        type: 'error',
        position: 'top',
        autoHide: true,
      });
    } finally {
      setLoadingCategories(false);
    }
  }

  async function loadModules(catId) {
    setLoadingModules(true);
    try {
      const q = query(
        collection(firestore, 'categories', catId, 'cards'),
        orderBy('order', 'asc')
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map(doc => ({
          id: doc.id,
          title: doc.data().title,
          order: doc.data().order ?? Number.MAX_SAFE_INTEGER,
        }))
        .sort((a, b) => a.order - b.order);
      setModules(list);
    } catch (e) {
      console.error('Erro ao carregar módulos', e);
      Toast.show({
        text1: 'Erro ao carregar módulos.',
        type: 'error',
        position: 'top',
        autoHide: true,
      });
    } finally {
      setLoadingModules(false);
    }
    setSelectedModule(null);
  }

  async function handleCopy() {
    if (isVideo && !selectedModule) return;

    // ✅ validação simples do custom
    let destinationPosition = { mode: 'top', position: 1 };

    if (isVideo) {
      if (positionMode === 'bottom') {
        destinationPosition = { mode: 'bottom' };
      } else if (positionMode === 'custom') {
        const parsed = parseInt(customPosition || '1', 10);
        const safePos = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
        destinationPosition = { mode: 'custom', position: safePos };
      } else {
        destinationPosition = { mode: 'top', position: 1 };
      }
    }

    setCopying(true);
    try {
      const functions = getFunctions();
      // Mesmo prazo da function (540s). Com o padrão (70s), um módulo grande
      // ou o Panda lento fazia o painel mostrar erro com a cópia feita no
      // servidor — e o admin copiava de novo, duplicando o conteúdo.
      const copyContent = httpsCallable(functions, 'copyContent', {
        timeout: 540000,
      });

      await copyContent({
        sourceCategoryId,
        sourceCardId,
        sourceVideoId: isVideo ? videoDocId : null,
        destinationCategoryId: selectedCategory,
        destinationCardId: isVideo ? selectedModule : null,

        // ✅ novo
        destinationPosition: isVideo ? destinationPosition : null,
      });

      Toast.show({
        text1: 'Conteúdo copiado com sucesso!',
        type: 'success',
        position: 'top',
        autoHide: true,
      });

      onSuccess && onSuccess();
    } catch (e) {
      console.error('Erro ao copiar conteúdo', e);
      Toast.show({
        text1: 'Erro ao copiar conteúdo.',
        type: 'error',
        position: 'top',
        autoHide: true,
      });
    } finally {
      setCopying(false);
      onDismiss();
    }
  }

  const isCopyDisabled =
    copying ||
    (isVideo && !selectedModule) ||
    (isVideo && positionMode === 'custom' && !customPosition);

  const renderStepLabel = (number, text) => (
    <View style={styles.stepRow}>
      <View style={styles.stepBadge}>
        <Text style={styles.stepBadgeText}>{number}</Text>
      </View>
      <Text style={styles.stepText}>{text}</Text>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
      <View style={styles.overlay}>
        <View style={[styles.card, { width: modalWidth, maxWidth: 560 }]}>
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>
                {isVideo ? 'Copiar conteúdo' : 'Copiar módulo'}
              </Text>
              <Text style={styles.subtitle}>
                {isVideo
                  ? 'Escolha para qual módulo o conteúdo será copiado. O original continua onde está.'
                  : 'Escolha para qual categoria o módulo será copiado. O original continua onde está.'}
              </Text>
            </View>
            <Pressable
              onPress={onDismiss}
              hitSlop={8}
              disabled={copying}
              style={({ hovered }) => [
                styles.closeBtn,
                hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
              ]}
            >
              <Ionicons name="close" size={20} color={ADMIN_COLORS.muted} />
            </Pressable>
          </View>

          <ScrollView
            style={{ maxHeight: '62vh' }}
            contentContainerStyle={{ padding: 20 }}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          >
            {/* 1. Categoria */}
            {renderStepLabel(1, 'Categoria de destino')}

            {loadingCategories ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={ADMIN_COLORS.accent} />
              </View>
            ) : (
              <View style={styles.listBox}>
                <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
                  {categories.map((item, index) => (
                    <SelectableRow
                      key={item.id}
                      order={formatOrder(item.order)}
                      title={item.title}
                      selected={selectedCategory === item.id}
                      last={index === categories.length - 1}
                      onPress={() => {
                        setSelectedCategory(item.id);
                        if (isVideo) loadModules(item.id);
                      }}
                    />
                  ))}
                </ScrollView>
              </View>
            )}

            {/* 2. Módulo */}
            {isVideo && (
              <>
                <View style={{ height: 20 }} />
                {renderStepLabel(2, 'Módulo de destino')}

                {loadingModules ? (
                  <View style={styles.loadingBox}>
                    <ActivityIndicator color={ADMIN_COLORS.accent} />
                  </View>
                ) : modules.length === 0 ? (
                  <View style={styles.emptyBox}>
                    <Text style={styles.emptyText}>
                      Não há módulo disponível nesta categoria.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.listBox}>
                    <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
                      {modules.map((item, index) => (
                        <SelectableRow
                          key={item.id}
                          order={formatOrder(item.order)}
                          title={item.title}
                          selected={selectedModule === item.id}
                          last={index === modules.length - 1}
                          onPress={() => setSelectedModule(item.id)}
                        />
                      ))}
                    </ScrollView>
                  </View>
                )}
              </>
            )}

            {/* 3. Posição */}
            {isVideo && (
              <>
                <View style={{ height: 20 }} />
                {renderStepLabel(3, 'Posição no módulo')}

                <SegmentedControl
                  options={POSITION_OPTIONS}
                  value={positionMode}
                  onChange={setPositionMode}
                />

                {positionMode === 'custom' && (
                  <View style={{ marginTop: 14 }}>
                    <Text style={styles.fieldLabel}>Posição</Text>
                    <AdminInput
                      value={customPosition}
                      keyboardType="numeric"
                      placeholder="Ex.: 3"
                      onChangeText={(t) => setCustomPosition(t.replace(/[^\d]/g, ''))}
                    />
                    <Text style={styles.hint}>
                      Informe um número a partir de 1. Se exceder o total, vai para o final.
                    </Text>
                  </View>
                )}
              </>
            )}
          </ScrollView>

          <View style={[styles.footer, isMobile && { flexDirection: 'column-reverse' }]}>
            <Pressable
              onPress={onDismiss}
              disabled={copying}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                isMobile && { width: '100%' },
                hovered && { backgroundColor: '#E5E7EB' },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.secondaryText }]}>
                Cancelar
              </Text>
            </Pressable>
            <Pressable
              onPress={handleCopy}
              disabled={isCopyDisabled}
              style={({ hovered }) => [
                styles.btn,
                styles.btnPrimary,
                isMobile && { width: '100%' },
                hovered && !isCopyDisabled && { backgroundColor: ADMIN_COLORS.accentDark },
                isCopyDisabled && { opacity: 0.5 },
              ]}
            >
              {copying ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={[styles.btnText, { color: '#FFFFFF' }]}>Copiar</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(17,24,39,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  title: { fontFamily: ADMIN_FONT, fontSize: 18, fontWeight: '700', color: ADMIN_COLORS.ink },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
    lineHeight: 18,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  stepRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  stepBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: ADMIN_COLORS.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepBadgeText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.accent,
  },
  stepText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.label },
  listBox: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    cursor: 'pointer',
  },
  rowDivider: { borderBottomWidth: 1, borderBottomColor: ADMIN_COLORS.border },
  orderPill: {
    minWidth: 30,
    height: 22,
    borderRadius: 11,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  orderText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.muted,
  },
  rowTitle: {
    flex: 1,
    minWidth: 0,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.ink,
  },
  loadingBox: { paddingVertical: 24, alignItems: 'center' },
  emptyBox: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    padding: 14,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  emptyText: { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted },
  fieldLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.label,
    marginBottom: 6,
  },
  hint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    marginTop: 6,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  btn: {
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
  btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
  btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600' },
});
